// Work kit undo stack (W8 Track C; created here because no merged wave had
// landed app/javascript/lib/work/undo.js at wave start — the W8 spec's
// file-ownership rule). Flow's undo machinery (flow_controller.js
// :2812-2870) ported to a stack-only helper: the ENGINE owns what a snapshot
// means and how it re-applies; this module owns the stack discipline only.
//
//   - push(presnapshot): PRE-state snapshots pushed once per gesture (the
//     flow presnapshot pattern — a gesture captures its pre-state at gesture
//     START and the push happens at commit, guarded no-push-on-noop).
//   - Stack cap: default 100 (flow _maxHistory); the OLDEST entry shifts out.
//   - undo()/redo() return the snapshot to restore (or null) and swap stacks;
//     the CALLER applies it through its converged replace path.
//   - `applying` is the _applyingSnapshot guard: while the engine restores a
//     snapshot, mutation paths must not push a second entry (restore emits
//     nothing and records nothing — the engine checks this flag).

const DEFAULT_CAP = 100

export function createUndoStack(cap = DEFAULT_CAP) {
  const undoStack = []
  const redoStack = []
  let applying = false

  const limit = cap && cap > 0 ? cap : DEFAULT_CAP

  function push(presnapshot = null) {
    if (applying) return false
    undoStack.push(presnapshot)
    redoStack.length = 0
    if (undoStack.length > limit) undoStack.shift()
    return true
  }

  return {
    push,
    // Pop the newest pre-state. The CURRENT state must be pushed to redo by
    // the caller BEFORE applying (the engine snapshots current state first,
    // flow parity). Returns the snapshot or null when the stack is empty.
    undo() {
      if (undoStack.length === 0) return null
      return undoStack.pop()
    },
    redo() {
      if (redoStack.length === 0) return null
      return redoStack.pop()
    },
    pushRedo(snapshot) {
      redoStack.push(snapshot)
    },
    // Drop every redo entry (the Esc-cancel of a nudge chain also invalidates
    // the chain-derived redo states).
    clearRedo() {
      redoStack.length = 0
    },
    undoDepth() {
      return undoStack.length
    },
    redoDepth() {
      return redoStack.length
    },
    // The restore guard (pin 20): true while a restore is in flight. The
    // engine sets/clears it around _applyBarState so restores push nothing
    // and emit nothing.
    get applying() {
      return applying
    },
    set applying(value) {
      applying = !!value
    },
    // Test seam: reset without changing the applying flag contract.
    clear() {
      undoStack.length = 0
      redoStack.length = 0
    },
    // Pop the undo stack back down to a recorded depth (the engine records
    // undoDepth() when a nudge CHAIN starts; Esc-cancel truncates the chain's
    // own entries so the cancelled chain is not re-undoable).
    truncate(depth) {
      if (!Number.isInteger(depth) || depth < 0) return
      while (undoStack.length > depth) undoStack.pop()
    },
  }
}
